<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('AI_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AI_SETTINGS.TITLE')"
        :description="$t('AI_SETTINGS.SUBTITLE')"
        icon-name="brain-circuit"
      />
    </template>
    
    <template #body>
      <!-- Tab Navigation -->
      <div class="border-b border-gray-200 mb-6">
        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            :class="[
              'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm',
              activeTab === tab.key
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
            @click="activeTab = tab.key"
          >
            {{ tab.name }}
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div v-if="activeTab === 'overview'">
        <overview-tab
          :ai-configuration="aiConfiguration"
          :is-loading="isLoading"
          @refresh="loadAiConfiguration"
        />
      </div>

      <div v-if="activeTab === 'configurations'">
        <ai-configurations-tab
          :configuration="aiConfiguration"
          :providers="providers"
          :is-loading="isLoading"
          @refresh="loadAiConfiguration"
        />
      </div>

      <div v-if="activeTab === 'business-data'">
        <business-data-tab />
      </div>

      <div v-if="activeTab === 'data-collection'">
        <data-collection-tab />
      </div>

      <div v-if="activeTab === 'image-library'">
        <image-library-tab />
      </div>

      <div v-if="activeTab === 'summary'">
        <conversation-summary-tab />
      </div>
    </template>
  </SettingsLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import { useAlert } from 'dashboard/composables'
import SettingsLayout from '../SettingsLayout.vue'
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue'
import OverviewTab from './components/OverviewTab.vue'
import AiConfigurationsTab from './components/AiConfigurationsTab.vue'
import BusinessDataTab from './components/BusinessDataTab.vue'
import ImageLibraryTab from './components/ImageLibraryTab.vue'
import DataCollectionTab from './components/DataCollectionTab.vue'
import ConversationSummaryTab from './components/ConversationSummaryTab.vue'

export default {
  name: 'AiSettings',
  components: {
    SettingsLayout,
    BaseSettingsHeader,
    OverviewTab,
    AiConfigurationsTab,
    BusinessDataTab,
    ImageLibraryTab,
    DataCollectionTab,
    ConversationSummaryTab,
  },
  
  data() {
    return {
      activeTab: 'overview',
      aiConfiguration: null,
      providers: [],
      isLoading: true,
    }
  },
  
  computed: {
    ...mapGetters(['getCurrentAccount']),
    currentAccount() {
      return this.getCurrentAccount;
    },
    tabs() {
      return [
        { key: 'overview', name: this.$t('AI_SETTINGS.TABS.OVERVIEW') },
        { key: 'configurations', name: this.$t('AI_SETTINGS.TABS.CONFIGURATIONS') },
        { key: 'business-data', name: this.$t('AI_SETTINGS.TABS.BUSINESS_DATA') },
        { key: 'data-collection', name: this.$t('AI_SETTINGS.TABS.DATA_COLLECTION') },
        { key: 'image-library', name: this.$t('AI_SETTINGS.TABS.IMAGE_LIBRARY') },
        { key: 'summary', name: this.$t('AI_SETTINGS.TABS.SUMMARY') },
      ]
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      if (this.$store.getters.getCurrentAccountId) {
        this.loadAiConfiguration()
        this.loadProviders()
      } else {
        setTimeout(() => {
          if (this.$store.getters.getCurrentAccountId) {
            this.loadAiConfiguration()
            this.loadProviders()
          }
        }, 500)
      }
    })
  },
  
  methods: {
    async loadAiConfiguration() {
      try {
        this.isLoading = true
        const accountId = this.$store.getters.getCurrentAccountId
        const response = await window.axios.get(`/api/v1/accounts/${accountId}/ai_configurations`)
        
        if (response.data && response.data !== null) {
          this.aiConfiguration = response.data
        } else {
          this.aiConfiguration = null
        }
      } catch (error) {
        console.error('Error loading AI configuration:', error)
        if (error.response && error.response.status === 404) {
          this.aiConfiguration = null
        } else {
          useAlert(this.$t('AI_SETTINGS.ERRORS.LOAD_FAILED'))
        }
      } finally {
        this.isLoading = false
      }
    },
    
    async loadProviders() {
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        const response = await window.axios.get(`/api/v1/accounts/${accountId}/ai_configurations/providers`)
        this.providers = response.data.providers
      } catch (error) {
        console.error('Error loading providers:', error)
      }
    },
  },
}
</script>

 